import React, { useEffect, useMemo, useState } from 'react';
import { SideBarModal } from '@/components/Modal';
import { Button } from '@/components';

type DecisionOption = {
  value: string;
  title: string;
  description: string;
  icon: string;
  accent: string;
  badge: string;
};

interface MakeDecisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: { decision: string; decisionNotes: string }) => void;
  options: DecisionOption[];
  isSubmitting?: boolean;
  headerTitle?: string;
  headerSubtitle?: string;
}

const MakeDecisionModal: React.FC<MakeDecisionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  options,
  isSubmitting = false,
  headerTitle = 'Stakeholder Approval',
  headerSubtitle = 'Please review the options below and submit your approval decision.',
}) => {
  const [decision, setDecision] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const activeCards = useMemo(
    () => options.filter((option) => Boolean(option.title)),
    [options]
  );

  const handleClose = () => {
    setDecision(null);
    setNotes('');
    onClose();
  };

  const handleSubmit = () => {
    if (!decision) return;
    onSubmit({ decision, decisionNotes: notes.trim() });
  };

  useEffect(() => {
    if (isOpen && activeCards.length > 0) {
      setDecision(activeCards[0].value);
    }
  }, [isOpen, activeCards]);

  const notesLength = notes.trim().length;
  const isNotesInvalid = notesLength > 0 && notesLength < 20;
  const isFormValid = Boolean(decision) && notesLength >= 20;

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleClose}
      position="center"
      width="lg"
      maxHeight="screen"
      showCloseButton
      className="max-w-[620px] !rounded-2xl"
      header={
        <div>
          <div className="text-base font-semibold text-gray-900">
            {headerTitle}
          </div>
          <div className="text-xs text-gray-500">{headerSubtitle}</div>
        </div>
      }
      footer={
        <div className="flex w-full justify-end gap-3">
          <Button
            onClick={handleClose}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-gray-600 hover:bg-gray-50"
            variant={'secondary'}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || isSubmitting}
            className="rounded-xl bg-purple-600 px-5 py-2 text-white hover:bg-purple-700"
          >
            {isSubmitting ? 'Saving...' : 'Submit Decisions'}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4">
        <div className="grid gap-3 sm:grid-cols-2">
          {activeCards.map((card) => {
            const isSelected = decision === card.value;
            return (
              <button
                key={card.value}
                type="button"
                onClick={() => setDecision(card.value)}
                className={`relative flex h-[145px] w-[272px] flex-col gap-2 rounded-2xl border px-5 py-4 text-left transition ${
                  isSelected
                    ? `${card.accent} bg-white shadow-sm`
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-3">
                    <div
                      className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                        card.badge
                      }`}
                    >
                      <i className={card.icon} />
                    </div>
                    <div>
                      <div className="text-base mb-2 font-medium text-gray-900">
                        {card.title}
                      </div>
                      <div className="text-xs mb-2 max-w-[200px] text-gray-500">
                        {card.description}
                      </div>
                    </div>
                  </div>
                  <span
                    className={`inline-flex h-4 w-4 items-center justify-center rounded-full border ${
                      isSelected
                        ? 'border-purple-500 text-purple-600'
                        : 'border-gray-300 text-transparent'
                    }`}
                  >
                    <span className="h-2 w-2 rounded-full bg-current" />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-5">
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Decision Notes<span className="text-red-500"> *</span>
          </label>
          <div className="relative">
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              placeholder="Provide notes for this decision"
              rows={4}
              minLength={20}
              className={`w-full rounded-md border px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 ${
                isNotesInvalid
                  ? 'border-red-500 focus:ring-red-200'
                  : 'border-gray-200'
              }`}
            />
            <div className="absolute bottom-2 right-3 text-[10px] text-gray-400">
              {Math.min(notes.length, 200)}/200
            </div>
            {isNotesInvalid && (
              <div className="mt-1 text-[11px] text-red-500">
                Minimum 20 characters
              </div>
            )}
          </div>
        </div>
      </div>
    </SideBarModal>
  );
};

export default MakeDecisionModal;
